import React from 'react';
import { useIntl } from 'react-intl';
import { Button, CircularProgress, Icon } from '@mui/material';
import { useOemFormatContext } from '../../hooks/useOemFormatContext';
import { useRemoteCommandsTab } from '../hooks/useRemoteCommandsTab';
import { LOCK_DOOR, UNLOCK_DOOR } from '../constants';
import { ConfirmRemoteCommandDialog } from './ConfirmRemoteCommandDialog';
import { RemoteCommandItem } from './RemoteCommandItem';
import { renderQueryState } from './renderQueryState';
import '../remote-commands.scss';

const ACTIONS = [
	{ command: LOCK_DOOR, icon: 'lock', labelKey: 'oemDataModal_remote_lock' },
	{ command: UNLOCK_DOOR, icon: 'lock_open', labelKey: 'oemDataModal_remote_unlock' },
];

/** The current command's card, or the empty / loading / error state. */
const CurrentCommand = ({ query, latest, formatDateTime, messages }) => {
	const state = renderQueryState(query, messages);
	if (state) return state;
	if (latest === null) {
		return <p className="oem-data-modal-message">{messages['oemDataModal_remote_empty']}</p>;
	}
	return <RemoteCommandItem command={latest} formatDateTime={formatDateTime} className="oem-remote-current" />;
};

/**
 * Status check of the active current command: a spinner while the first minute
 * of automatic checks runs, then a "Check status" button with its 20 s countdown.
 */
const StatusCheck = ({ statusCheck, messages, formatMessage }) => {
	if (statusCheck.phase === 'auto') {
		return (
			<p className="oem-remote-status-check" role="status">
				<CircularProgress size={14} />
				{messages['oemDataModal_remote_checking']}
			</p>
		);
	}
	if (statusCheck.phase !== 'manual') return null;
	const waiting = statusCheck.cooldownSeconds > 0;
	return (
		<div className="oem-remote-status-check">
			<span>{messages['oemDataModal_remote_stillInProgress']}</span>
			<Button
				variant="outlined"
				size="small"
				startIcon={statusCheck.checking ? <CircularProgress size={14} /> : <Icon>refresh</Icon>}
				disabled={waiting || statusCheck.checking}
				onClick={statusCheck.checkNow}
				className="oem-remote-status-check-button"
			>
				{waiting
					? formatMessage({ id: 'oemDataModal_remote_checkStatusIn' }, { seconds: statusCheck.cooldownSeconds })
					: messages['oemDataModal_remote_checkStatus']}
			</Button>
		</div>
	);
};

/**
 * "Remote commands" tab of OemDataModal (CerebrumX, behind
 * `cerebrumx-remote-door-commands`): equal-width Lock / Unlock doors buttons
 * (with confirmation) and the live status of the current command, checked with
 * the provider while it is active. The full list lives in the Command history
 * tab. Times use the user's time zone.
 */
export const RemoteCommandsTab = ({ record, unitLabel }) => {
	const { messages, formatMessage } = useIntl();
	const { formatDateTime } = useOemFormatContext();
	const tab = useRemoteCommandsTab(record.unitId);

	return (
		<div className="oem-tab-content oem-remote-commands">
			<div className="oem-remote-actions">
				{ACTIONS.map(({ command, icon, labelKey }) => (
					<Button
						key={command}
						variant="contained"
						color="primary"
						disableElevation
						fullWidth
						startIcon={<Icon>{icon}</Icon>}
						disabled={tab.disabled}
						onClick={() => tab.request(command)}
						className="oem-remote-action"
					>
						{messages[labelKey]}
					</Button>
				))}
			</div>
			<section aria-label={messages['oemDataModal_remote_current']}>
				<h3 className="oem-section-title">{messages['oemDataModal_remote_current']}</h3>
				<CurrentCommand query={tab.query} latest={tab.latest} formatDateTime={formatDateTime} messages={messages} />
				<StatusCheck statusCheck={tab.statusCheck} messages={messages} formatMessage={formatMessage} />
			</section>
			<ConfirmRemoteCommandDialog
				command={tab.pending}
				vehicleName={unitLabel || String(record.unitId)}
				onConfirm={tab.confirm}
				onCancel={tab.cancel}
			/>
		</div>
	);
};
